import React from "react";
import { toast } from "sonner";
import { RefreshCw, Shield, Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { adminApi } from "../lib/api";

const ACTION_LABEL = {
  delete_booking: { label: "Slettet booking", color: "bg-red-100 text-red-900", icon: Trash2 },
  delete_driver: { label: "Slettet chauffør", color: "bg-red-100 text-red-900", icon: Trash2 },
  delete_contact: { label: "Slettet besked", color: "bg-red-100 text-red-900", icon: Trash2 },
  delete_review: { label: "Slettet anmeldelse", color: "bg-red-100 text-red-900", icon: Trash2 },
  delete_business_lead: { label: "Slettet B2B-lead", color: "bg-red-100 text-red-900", icon: Trash2 },
  reset_test_data: { label: "Nulstilling af test-data", color: "bg-amber-100 text-amber-900", icon: RotateCcw },
};

function ActionBadge({ action }) {
  const meta = ACTION_LABEL[action] || { label: action, color: "bg-slate-100 text-slate-900", icon: Shield };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold ${meta.color}`}>
      <Icon size={12} /> {meta.label}
    </span>
  );
}

function DetailsCell({ details, action }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-brand-text2 text-xs">—</span>;
  if (action === "reset_test_data") {
    return (
      <div className="text-xs text-brand-text2">
        <span className="font-semibold text-brand-dark">{details.total ?? 0}</span> dokumenter slettet
        {details.deleted && Object.keys(details.deleted).length > 0 && (
          <span className="ml-1">({Object.entries(details.deleted).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join(", ")})</span>
        )}
      </div>
    );
  }
  const pairs = Object.entries(details).filter(([, v]) => v !== null && v !== "");
  return (
    <div className="text-xs text-brand-text2 space-y-0.5">
      {pairs.map(([k, v]) => (
        <div key={k}><span className="text-brand-dark/60">{k}:</span> <span className="font-mono">{String(v).slice(0, 80)}</span></div>
      ))}
    </div>
  );
}

export default function AuditLogPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/audit-log", { params: { limit: 200 } });
      setItems(data || []);
    } catch {
      toast.error("Kunne ikke hente audit log");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading && items.length === 0) return <div className="text-brand-text2" data-testid="audit-loading">Indlæser audit log...</div>;

  return (
    <div className="space-y-5" data-testid="audit-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="kicker">Compliance · GDPR · Sporbarhed</div>
          <h3 className="font-display font-semibold text-2xl text-brand-dark mt-2">Audit log</h3>
          <p className="text-sm text-brand-text2 mt-1">Hver slet- og nulstillingshandling logges automatisk. De seneste 200 vises her.</p>
        </div>
        <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="audit-refresh">
          <RefreshCw size={14} /> Genindlæs
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="audit-empty">
          <Shield size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen handlinger logget endnu</div>
          <p className="text-sm text-brand-text2 mt-1">Når du sletter en booking, chauffør eller besked, vises det her.</p>
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-brand-text2 uppercase tracking-widest border-b border-brand bg-brand-bg2">
              <tr>
                <th className="px-4 py-3">Tidspunkt</th>
                <th className="px-4 py-3">Bruger</th>
                <th className="px-4 py-3">Handling</th>
                <th className="px-4 py-3">Detaljer</th>
                <th className="px-4 py-3">Target ID</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-brand align-top">
                  <td className="px-4 py-3 text-xs text-brand-text2 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString("da-DK")}
                  </td>
                  <td className="px-4 py-3 font-semibold text-brand-dark whitespace-nowrap">{e.admin_email}</td>
                  <td className="px-4 py-3"><ActionBadge action={e.action} /></td>
                  <td className="px-4 py-3"><DetailsCell details={e.details} action={e.action} /></td>
                  <td className="px-4 py-3 text-xs text-brand-text2 font-mono">{e.target_id ? e.target_id.slice(0, 8) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="audit-info">
        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
        <div>
          <strong>Bemærk:</strong> Audit-loggen er en sikkerhedskopi af hvad der blev slettet (ID, navn, email osv.) — selve det slettede dokument kan IKKE gendannes. Ved kritiske sletninger (regnskab, faktura) bør du overveje at eksportere PDF først.
        </div>
      </div>
    </div>
  );
}
